import React, { useEffect, useState } from "react";
import { API, adminHeaders, adminWrite } from "@/lib/api";
import { ArrowUp, ArrowDown, Plus, Trash2 } from "lucide-react";

const SETTINGS_KEYS = ["couverture", "chapo", "promesses", "logos", "ticker", "expertise", "realisationsSec", "canaux", "methode", "faq", "contactSec", "marque", "home", "guide_regies"];
const COLLECTION_TABS = [
  { key: "services", label: "Services" },
  { key: "projects", label: "Réalisations" },
  { key: "posts", label: "Articles" },
  { key: "pages", label: "Pages" },
  { key: "regies_guide", label: "Guide régies" },
];

function JsonEditor({ value, onSave, onDelete, saving }) {
  const isStringList = Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === "string");
  const [text, setText] = useState(isStringList ? value.join("\n") : JSON.stringify(value, null, 2));
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  useEffect(() => {
    setText(isStringList ? value.join("\n") : JSON.stringify(value, null, 2));
    setErr("");
  }, [value, isStringList]);
  const save = async () => {
    let parsed;
    try {
      parsed = isStringList
        ? text.split("\n").map((l) => l.trim()).filter(Boolean)
        : JSON.parse(text);
    } catch (e) {
      setOk(false);
      setErr("JSON invalide : " + e.message);
      return;
    }
    setErr("");
    setOk(false);
    try {
      await onSave(parsed);
      setOk(true);
    } catch (e) {
      setErr("Enregistrement REFUSÉ par le serveur — " + e.message + " Rien n'a été écrit ; votre saisie est conservée ci-dessus.");
    }
  };
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {isStringList && <p style={{ fontSize: 13, color: "var(--ink3)", margin: 0 }} data-testid="admin-list-hint">Une entrée par ligne (les lignes vides sont ignorées).</p>}
      <textarea className="admin-textarea" value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} data-testid="admin-json-editor" />
      {err && <p style={{ color: "#8C1D18", fontSize: 13, margin: 0, whiteSpace: "pre-wrap" }} data-testid="admin-json-error">{err}</p>}
      {ok && !err && <p style={{ color: "#1B5E20", fontSize: 13, margin: 0 }} data-testid="admin-json-saved">Enregistré et relu depuis la base.</p>}
      <div style={{ display: "flex", gap: 12 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="admin-save">{saving ? "Enregistrement…" : "Enregistrer"}</button>
        {onDelete && <button className="btn" onClick={onDelete} data-testid="admin-delete"><Trash2 size={13} /> Supprimer</button>}
      </div>
    </div>
  );
}

export default function AdminPage() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
  const [token, setToken] = useState(localStorage.getItem("sliz_admin_token") || "");
  const [email, setEmail] = useState("admin@sliz.fr");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [tab, setTab] = useState("site");
  const [data, setData] = useState(null);
  const [selKey, setSelKey] = useState("couverture");
  const [selSlug, setSelSlug] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [audioUrls, setAudioUrls] = useState({});
  const [mediaList, setMediaList] = useState([]);
  const [uploadPct, setUploadPct] = useState(null);
  const [uploadMsg, setUploadMsg] = useState("");
  const [flash, setFlash] = useState("");

  const load = async () => {
    const res = await fetch(`${API}/content`);
    setData(await res.json());
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!token || tab !== "demandes") return;
    fetch(`${API}/admin/contacts`, { headers: adminHeaders() })
      .then((r) => { if (r.status === 401) { localStorage.removeItem("sliz_admin_token"); setToken(""); return []; } return r.ok ? r.json() : []; })
      .then(setContacts);
  }, [token, tab]);
  useEffect(() => {
    if (!token || tab !== "briefs") return;
    fetch(`${API}/admin/briefs`, { headers: adminHeaders() })
      .then((r) => { if (r.status === 401) { localStorage.removeItem("sliz_admin_token"); setToken(""); return []; } return r.ok ? r.json() : []; })
      .then(setBriefs);
  }, [token, tab]);

  const loadAudio = async (id) => {
    if (audioUrls[id]) return;
    const res = await fetch(`${API}/admin/briefs/${id}/audio`, { headers: adminHeaders() });
    if (!res.ok) return;
    const b = await res.blob();
    setAudioUrls((u) => ({ ...u, [id]: URL.createObjectURL(b) }));
  };

  const loadMedia = () => fetch(`${API}/admin/media`, { headers: adminHeaders() }).then((r) => { if (r.status === 401) { localStorage.removeItem("sliz_admin_token"); setToken(""); return []; } return r.ok ? r.json() : []; }).then(setMediaList);
  useEffect(() => {
    if (!token || tab !== "medias") return;
    loadMedia();
  }, [token, tab]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploadMsg("");
    setUploadPct(0);
    try {
      const init = await fetch(`${API}/admin/media/upload/init`, {
        method: "POST", headers: adminHeaders(), body: JSON.stringify({ filename: file.name }),
      });
      if (!init.ok) throw new Error((await init.json()).detail || "Format non accepté");
      const { upload_id } = await init.json();
      const CHUNK = 4 * 1024 * 1024;
      for (let off = 0; off < file.size; off += CHUNK) {
        const fd = new FormData();
        fd.append("chunk", file.slice(off, off + CHUNK));
        const r = await fetch(`${API}/admin/media/upload/${upload_id}/chunk`, {
          method: "POST", headers: { Authorization: adminHeaders().Authorization }, body: fd,
        });
        if (!r.ok) throw new Error("Envoi interrompu, réessayez.");
        setUploadPct(Math.min(99, Math.round(((off + CHUNK) / file.size) * 100)));
      }
      const done = await fetch(`${API}/admin/media/upload/${upload_id}/complete`, { method: "POST", headers: adminHeaders() });
      if (!done.ok) throw new Error("Finalisation impossible, réessayez.");
      const d = await done.json();
      setUploadPct(null);
      setUploadMsg(`Fichier en ligne : ${d.url}`);
      loadMedia();
    } catch (e) {
      setUploadPct(null);
      setUploadMsg(`Erreur : ${e.message}`);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoginErr("");
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setLoginErr(typeof d.detail === "string" ? d.detail : "Connexion refusée");
      return;
    }
    const d = await res.json();
    localStorage.setItem("sliz_admin_token", d.token);
    setToken(d.token);
  };

  if (!token) {
    return (
      <main style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--pp2)" }} data-testid="admin-login">
        <form onSubmit={login} style={{ width: 360, background: "#fff", border: "1px solid var(--li)", padding: 40, display: "grid", gap: 22 }}>
          <span className="t-logo" style={{ fontSize: 20 }}>SLI<span style={{ color: "var(--roi)" }}>Z</span></span>
          <p className="t-label" style={{ margin: 0 }}>Back-office</p>
          <div className="field">
            <label htmlFor="a-email">Email</label>
            <input id="a-email" data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="a-pass">Mot de passe</label>
            <input id="a-pass" data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {loginErr && <p style={{ color: "#8C1D18", fontSize: 13, margin: 0 }} data-testid="admin-login-error">{loginErr}</p>}
          <button className="btn btn-primary" type="submit" data-testid="admin-login-submit">Connexion</button>
        </form>
      </main>
    );
  }

  if (!data) return null;

  const putSetting = async (val) => {
    setSaving(true);
    try {
      await adminWrite(`/admin/settings/${selKey}`, { method: "PUT", body: { value: val } });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const collItems = (key) => (key === "pages" ? Object.values(data.pages || {}) : data[key] || []);
  const currentColl = COLLECTION_TABS.find((t) => t.key === tab);
  const items = currentColl ? collItems(tab) : [];
  const selItem = items.find((x) => x.slug === selSlug) || items[0];

  const saveItem = async (val) => {
    setSaving(true);
    try {
      await adminWrite(`/admin/${tab}/${selItem.slug}`, { method: "PUT", body: val });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const addItem = async () => {
    const slug = `nouveau-${Date.now().toString(36)}`;
    const base = tab === "pages"
      ? { slug, seoTitle: "", seoMeta: "", h1: "Nouvelle page", sections: [] }
      : tab === "projects"
        ? { slug, ordre: items.length + 1, titre: "Nouveau projet", categorie: "Publicités", client: "", annee: "2026", canal: "", duree: "", service: "film-publicitaire", videoUrl: "", youtube_id: "", date_mise_en_ligne: "", role: "", resume: "" }
        : tab === "regies_guide"
          ? { slug, ordre: items.length + 1, actif: false, canal: "tv_segmentee", nom: "", sous_titre: "", question: "", prefixe_cle: "", chiffre_cle: "", fiche_liste: false, ticket_montant: "", achat: "", reperes: [{ libelle: "", montant_texte: "", montant_min: "", montant_max: "", type: "releve", confiance: "eleve", source_nom: "", source_url: "", source_date: "", actif: false }], recommandation: { titre: "", enveloppe_texte: "", texte: "", actif: false } }
        : tab === "posts"
          ? { slug, ordre: items.length + 1, titre: "Nouvel article", date: "2026-09-01", excerpt: "", services: [], sections: [] }
          : { slug, ordre: items.length + 1, kind: "service", parent: "production-audiovisuelle-paris", seoTitle: "", seoMeta: "", h1: "Nouveau service", chapeau: "", sections: [], faq: [] };
    try {
      await adminWrite(`/admin/${tab}`, { method: "POST", body: base });
      setFlash("");
      await load();
      setSelSlug(slug);
    } catch (e) {
      setFlash("Création REFUSÉE — " + e.message);
    }
  };

  const delItem = async () => {
    if (!window.confirm("Supprimer définitivement cette entrée ?")) return;
    try {
      await adminWrite(`/admin/${tab}/${selItem.slug}`, { method: "DELETE" });
      setFlash("");
      setSelSlug(null);
      await load();
    } catch (e) {
      setFlash("Suppression REFUSÉE — " + e.message);
    }
  };

  const move = async (slug, dir) => {
    const ordered = [...items].sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
    const i = ordered.findIndex((x) => x.slug === slug);
    const j = i + dir;
    if (j < 0 || j >= ordered.length) return;
    [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
    try {
      await adminWrite(`/admin/${tab}/reorder`, { method: "POST", body: { slugs: ordered.map((x) => x.slug) } });
      setFlash("");
      await load();
    } catch (e) {
      setFlash("Réordonnancement REFUSÉ — " + e.message);
    }
  };

  return (
    <main style={{ minHeight: "100vh", background: "var(--pp2)" }} data-testid="admin-panel">
      <div style={{ background: "#fff", borderBottom: "1px solid var(--li)" }}>
        <div className="wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <span className="t-logo" style={{ fontSize: 16 }}>SLI<span style={{ color: "var(--roi)" }}>Z</span></span>
          <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
            <a href="/" className="t-label" data-testid="admin-view-site">Voir le site</a>
            <button className="t-label" style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="admin-logout"
              onClick={() => { localStorage.removeItem("sliz_admin_token"); setToken(""); }}>
              Déconnexion
            </button>
          </div>
        </div>
        <div className="wrap" style={{ display: "flex", gap: 28, paddingBottom: 14, flexWrap: "wrap" }}>
          <button className={`admin-tab ${tab === "site" ? "active" : ""}`} onClick={() => setTab("site")} data-testid="admin-tab-site">Contenu du site</button>
          {COLLECTION_TABS.map((t) => (
            <button key={t.key} className={`admin-tab ${tab === t.key ? "active" : ""}`} onClick={() => { setTab(t.key); setSelSlug(null); }} data-testid={`admin-tab-${t.key}`}>{t.label}</button>
          ))}
          <button className={`admin-tab ${tab === "demandes" ? "active" : ""}`} onClick={() => setTab("demandes")} data-testid="admin-tab-demandes">Demandes reçues</button>
          <button className={`admin-tab ${tab === "briefs" ? "active" : ""}`} onClick={() => setTab("briefs")} data-testid="admin-tab-briefs">Briefs vocaux</button>
          <button className={`admin-tab ${tab === "medias" ? "active" : ""}`} onClick={() => setTab("medias")} data-testid="admin-tab-medias">Médias</button>
        </div>
      </div>

      {flash && (
        <div className="wrap" style={{ paddingTop: 18 }}>
          <p style={{ margin: 0, padding: "12px 16px", background: "#FDECEA", border: "1px solid #8C1D18", color: "#8C1D18", fontSize: 13 }} data-testid="admin-flash">{flash}</p>
        </div>
      )}

      <div className="wrap" style={{ paddingTop: 40, paddingBottom: 80 }}>
        {tab === "site" && (
          <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
            <div>
              {SETTINGS_KEYS.map((k) => (
                <button key={k} onClick={() => setSelKey(k)} className="admin-tab" style={{ display: "block", padding: "10px 0", border: "none", color: selKey === k ? "var(--roi)" : "var(--ink2)" }} data-testid={`admin-setting-${k}`}>
                  {k}
                </button>
              ))}
            </div>
            <div>
              <p className="t-label" style={{ marginBottom: 14 }}>Bloc « {selKey} » — modifiable en direct, publié immédiatement</p>
              <JsonEditor value={data.settings?.[selKey]} onSave={putSetting} saving={saving} />
            </div>
          </div>
        )}

        {currentColl && (
          <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
            <div>
              <button className="btn" onClick={addItem} style={{ marginBottom: 20, padding: "10px 16px" }} data-testid="admin-add"><Plus size={13} /> Ajouter</button>
              {items.map((it) => (
                <div key={it.slug} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid var(--li2)" }}>
                  <button onClick={() => setSelSlug(it.slug)} style={{ flex: 1, textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "Archivo", color: selItem?.slug === it.slug ? "var(--roi)" : "var(--ink)" }} data-testid={`admin-item-${it.slug}`}>
                    {it.titre || it.h1 || it.nom || it.slug}
                  </button>
                  {tab !== "pages" && (
                    <>
                      <button onClick={() => move(it.slug, -1)} aria-label="Monter" style={{ background: "none", border: "none", cursor: "pointer" }} data-testid={`admin-up-${it.slug}`}><ArrowUp size={13} /></button>
                      <button onClick={() => move(it.slug, 1)} aria-label="Descendre" style={{ background: "none", border: "none", cursor: "pointer" }} data-testid={`admin-down-${it.slug}`}><ArrowDown size={13} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div>
              {selItem && (
                <>
                  <p className="t-label" style={{ marginBottom: 14 }}>{selItem.slug}</p>
                  <JsonEditor value={selItem} onSave={saveItem} onDelete={delItem} saving={saving} />
                </>
              )}
            </div>
          </div>
        )}

        {tab === "medias" && (
          <div data-testid="admin-medias">
            <p className="t-label" style={{ marginBottom: 10 }}>Médiathèque — vidéos (mp4) et images (jpg, png, webp, svg)</p>
            <p className="t-body" style={{ fontSize: 14, color: "var(--ink2)", margin: "0 0 22px", maxWidth: 640 }}>
              Envoyez un fichier, puis copiez son URL et collez-la dans le champ voulu : « videoUrl » ou « poster » d'une réalisation, « videoPaysage » / « videoPortrait » de la couverture, ou un logo. Les fichiers sont stockés de façon pérenne et survivent aux redéploiements.
            </p>
            <label className="btn btn-primary" style={{ cursor: "pointer", display: "inline-flex" }} data-testid="admin-upload-label">
              {uploadPct === null ? "Envoyer un fichier" : `Envoi… ${uploadPct}%`}
              <input type="file" accept=".mp4,.jpg,.jpeg,.png,.webp,.svg" style={{ display: "none" }} disabled={uploadPct !== null}
                onChange={(e) => { uploadFile(e.target.files[0]); e.target.value = ""; }} data-testid="admin-upload-input" />
            </label>
            {uploadPct !== null && (
              <div style={{ maxWidth: 420, height: 3, background: "var(--li)", marginTop: 16 }}>
                <div style={{ width: `${uploadPct}%`, height: "100%", background: "var(--roi)", transition: "width 0.3s" }} />
              </div>
            )}
            {uploadMsg && <p className="t-body" style={{ fontSize: 14, marginTop: 16 }} data-testid="admin-upload-msg">{uploadMsg}</p>}
            <div style={{ marginTop: 36 }}>
              {mediaList.map((m) => (
                <div key={m.name} style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 0", borderBottom: "1px solid var(--li2)", flexWrap: "wrap" }} data-testid={`admin-media-${m.name}`}>
                  <span style={{ flex: 1, fontSize: 13, fontFamily: "Archivo", minWidth: 220 }}>{m.name}</span>
                  <span className="t-label">{(m.size / 1e6).toFixed(1)} Mo</span>
                  <button className="btn" style={{ padding: "6px 12px", fontSize: 12 }} data-testid={`admin-media-copy-${m.name}`}
                    onClick={() => { navigator.clipboard.writeText(m.url); setUploadMsg(`URL copiée : ${m.url}`); }}>
                    Copier l'URL
                  </button>
                  <button className="btn" style={{ padding: "6px 12px", fontSize: 12, color: "#8C1D18" }} data-testid={`admin-media-del-${m.name}`}
                    onClick={async () => {
                      if (!window.confirm(`Supprimer ${m.name} ? Les pages qui l'utilisent perdront leur média.`)) return;
                      try {
                        await adminWrite(`/admin/media/${m.name}`, { method: "DELETE" });
                        setUploadMsg("");
                        loadMedia();
                      } catch (err) {
                        setUploadMsg("Suppression REFUSÉE — " + err.message);
                      }
                    }}>
                    Supprimer
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "briefs" && (
          <div data-testid="admin-briefs">
            <p className="t-label" style={{ marginBottom: 20 }}>{briefs.length} brief(s) vocaux</p>
            {briefs.length === 0 && <p className="t-body">Aucun brief vocal pour le moment.</p>}
            {briefs.map((b) => (
              <div key={b.id} style={{ borderTop: "1px solid var(--li)", padding: "22px 0" }} data-testid={`admin-brief-${b.id}`}>
                <p style={{ margin: 0, fontVariationSettings: '"wdth" 100, "wght" 700' }}>
                  {b.nom} — {b.societe || "—"}
                  <span className="t-label" style={{ marginLeft: 10 }}>{b.status}</span>
                </p>
                <p className="t-body" style={{ margin: "6px 0 0", fontSize: 14 }}>{b.email} · {new Date(b.created_at).toLocaleString("fr-FR")}</p>
                <div style={{ margin: "14px 0" }}>
                  {audioUrls[b.id]
                    ? <audio controls src={audioUrls[b.id]} style={{ width: "100%", maxWidth: 480 }} data-testid={`admin-brief-audio-${b.id}`} />
                    : <button className="btn" style={{ padding: "8px 14px" }} onClick={() => loadAudio(b.id)} data-testid={`admin-brief-listen-${b.id}`}>Écouter l'enregistrement</button>}
                </div>
                {b.transcript && (
                  <details style={{ marginTop: 10 }}>
                    <summary className="t-label" style={{ cursor: "pointer" }}>Transcription</summary>
                    <p className="t-body" style={{ fontSize: 14, whiteSpace: "pre-wrap", background: "#fff", border: "1px solid var(--li)", padding: 16, marginTop: 10 }}>{b.transcript}</p>
                  </details>
                )}
                {b.proposition && (
                  <details style={{ marginTop: 10 }}>
                    <summary className="t-label" style={{ cursor: "pointer" }}>Proposition IA (production + diffusion)</summary>
                    <p className="t-body" style={{ fontSize: 14, whiteSpace: "pre-wrap", background: "#fff", border: "1px solid var(--li)", padding: 16, marginTop: 10 }}>{b.proposition}</p>
                  </details>
                )}
                {b.error && <p className="t-body" style={{ color: "#8C1D18", fontSize: 13, marginTop: 10 }}>Erreur : {b.error}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === "demandes" && (
          <div data-testid="admin-contacts">
            <p className="t-label" style={{ marginBottom: 20 }}>{contacts.length} demande(s)</p>
            {contacts.length === 0 && <p className="t-body">Aucune demande pour le moment.</p>}
            {contacts.map((c, i) => (
              <div key={i} style={{ borderTop: "1px solid var(--li)", padding: "18px 0" }} data-testid={`admin-contact-${i}`}>
                <p style={{ margin: 0, fontVariationSettings: '"wdth" 100, "wght" 700' }}>{c.nom} — {c.societe || "—"} <span className="t-label" style={{ marginLeft: 10 }}>{c.type}</span></p>
                <p className="t-body" style={{ margin: "6px 0 0", fontSize: 14 }}>{c.email} · {c.telephone || "—"} · {c.besoin || "—"} · {c.budget || "—"} · {c.echeance || "—"}</p>
                {c.message && <p className="t-body" style={{ margin: "6px 0 0", fontSize: 14 }}>{c.message}</p>}
                <p className="t-label" style={{ margin: "6px 0 0" }}>{c.created_at}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
